import {Injectable} from '@angular/core';
import {Observable, Subject} from "rxjs";
import {OverlayComponentData} from "./overlay-component-data";
import {OverlayWindowComponent} from "../shared/overlay-window/overlay-window.component";
import {OverlayWindowData} from "./overlay-window-data";
import {DisplayService} from "../services/display.service";
import {ShareOverlayComponent} from "../shared/share-overlay/share-overlay.component";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {AlertComponent} from "../shared/alert/alert.component";
import {head} from "lodash";
import {PromptTextComponent} from "../shared/prompt-text/prompt-text.component";

@Injectable({
  providedIn: 'root'
})
export class OverlayService {

  private _overlayAddChanges = new Subject<OverlayComponentData>();
  private _overlayCloseChanges = new Subject<number>();
  private _idCounter = 0;
  private _currentOverlays: OverlayComponentData[] = [];
  private _onEscPressedHandler = this.onEscPressed.bind(this);
  private _escListenerActive = false;

  constructor(
    private display: DisplayService,
    private store: Store<AppState>) {
  }

  private onEscPressed($event: KeyboardEvent) {
    if($event.key === 'Escape') {
      if(this._currentOverlays.length) {
        const targetOverlay = this._currentOverlays[this._currentOverlays.length - 1];
        const id = targetOverlay.id;
        if(id !== undefined) {
          const block = typeof targetOverlay.blockExternalClose === 'function'
            ? targetOverlay.blockExternalClose()
            : targetOverlay.blockExternalClose;
          if(!block) {
            this.removeComponent(id);
          }
        }
      }

    }
  }

  private setEscListenerState(state: boolean) {
    if(state) {
      window.addEventListener("keydown", this._onEscPressedHandler);
    } else {
      window.removeEventListener("keydown", this._onEscPressedHandler);
    }

    this._escListenerActive = state;

  }

  addComponent(component: OverlayComponentData): number {
    const nextID = this._idCounter++;
    component.id = nextID;
    // console.log(component);
    this._currentOverlays.push(component);
    this._overlayAddChanges.next(component);
    if(!this._escListenerActive) {
      this.setEscListenerState(true)
    }
    return nextID;
  }

  removeComponent(id: number) {
    const overlayIdx = this._currentOverlays.findIndex((el) => el.id === id);
    if(overlayIdx !== -1) {
      this._currentOverlays[overlayIdx].componentRef?.destroy();
    }
    this._currentOverlays = this._currentOverlays.filter((overlay) => overlay.id !== id);
    this._overlayCloseChanges.next(id);
    if(this._currentOverlays.length === 0) {
      this.setEscListenerState(false);
      this.display.enableBodyScroll();
    }
  }

  removeLastComponent() {
    const length = this._currentOverlays.length;
    if(length) {
      const id = this._currentOverlays[length - 1].id;
      if(id !== undefined && id !== null) {
        this.removeComponent(id);
      }
    }
  }

  onComponentAdded(): Observable<OverlayComponentData> {
    return this._overlayAddChanges;
  }

  onComponentRemoved(): Observable<number> {
    return this._overlayCloseChanges;
  }

  public openWindowOverlay(data: OverlayWindowData) {

    // it prevents opening component and opens native share device functionality
    if(data.componentMain.component === ShareOverlayComponent) { // TODO: add function for this case
      if('share' in window.navigator) {
        window.navigator.share({
          url: data.componentMain.data?.['url'] ?? window.location.href,
          text: "Schau, was ich auf berufsreise.at gefunden habe!", // TODO text
          title: "www.berufsreise.at", // TODO title
        });
        return -1;
      }
    }

    return this.addComponent({
      component: OverlayWindowComponent,
      data: {
        data: data
      },
      blockExternalClose: data.blockExternalClose
    });
  }

  alertMessage(title: string, text: string): void {
    this.openWindowOverlay({
      title,
      componentMain: {
        component: AlertComponent,
        data: {
          text
        }
      }
    })
  }

  prompt(headerTitle: string, label: string, currentValue: string, minLength: number, submitCallback: (text: string) => void, cancelCallback: () => void) {
    const windowId = this.openWindowOverlay({
      title: headerTitle,
      componentMain: {
        component: PromptTextComponent,
        data: {
          label,
          currentValue,
          minLength,
          confirmCallback: (text: string) => {
            submitCallback(text);
          },
          cancelCallback: () => {
            cancelCallback();
          }
        }
      },
      onCancel: () => {
        cancelCallback();
      }
    })
  }

}

